// Bounded undo history for the graph canvases (ENG-878).
//
// Each entry is a full { nodes, edges } snapshot taken IMMEDIATELY BEFORE a
// destructive change (node/edge deletion). One snapshot per delete GESTURE:
// the canvases push from ReactFlow's onBeforeDelete — which fires once with
// the complete pre-delete state — so a Delete-key removal of a node AND its
// attached edges is one history entry, not two.
//
// Snapshots hold the canvases' base node objects (render-time handler
// injection never reaches this state), so restoring them is a plain
// setNodes/setEdges — the editors' existing sync effects then propagate to
// the module graph refs and the localStorage autosave.
//
// Deliberately undo-only (no redo): the ticket's contract is that deleting
// nodes must be recoverable. Bounded so a long editing session can't grow
// memory without limit.
//
// A DECLARATION snapshot is the same stack with a `restore` callback. Deleting
// a variable or a function from the MY BLUEPRINT rail edits more than one
// canvas at once (the event graph, its functions, the body and face graphs, and
// the declaration list itself), and none of that is a { nodes, edges } pair the
// canvas could put back on its own. So the delete pushes a closure that knows
// how, and the canvas's undo listener runs it instead of setNodes/setEdges.
// Same stack on purpose: Ctrl+Z walks back through node deletes and
// declaration deletes in the order they happened, which is what an undo is.

export function createGraphHistory(limit = 50) {
  const stack = []
  return {
    /**
     * @param {Array} nodes  pre-change nodes
     * @param {Array} edges  pre-change edges
     * @param {Function} [restore]  when given, undoing this entry CALLS it
     *        instead of restoring nodes/edges (a declaration snapshot)
     */
    push(nodes, edges, restore) {
      stack.push(typeof restore === 'function' ? { nodes, edges, restore } : { nodes, edges })
      if (stack.length > limit) stack.shift()
    },
    pop() {
      return stack.pop() ?? null
    },
    /** The entry `pop()` would return, without removing it. */
    peek() {
      return stack[stack.length - 1] ?? null
    },
    get size() {
      return stack.length
    },
    clear() {
      stack.length = 0
    },
  }
}

// True for the plain undo chord (Ctrl/Cmd+Z, no shift) fired OUTSIDE an
// editable field — typing undo inside a node's text input must keep the
// browser's native text undo.
//
// A field marked `data-graph-undo` is the exception: its edits are graph undo
// steps (the MY BLUEPRINT variable values), and its value is also changed by
// the drag slider and the wheel, which the browser's text undo never sees. So
// Ctrl+Z there walks the graph's history like it does on the canvas.
export function isUndoKeyEvent(e) {
  if (!e || !(e.ctrlKey || e.metaKey) || e.shiftKey) return false
  if (String(e.key ?? '').toLowerCase() !== 'z') return false
  const t = e.target
  if (t?.dataset && 'graphUndo' in t.dataset) return true
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return false
  return true
}
